import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import CloseFullscreen from "@mui/icons-material/CloseFullscreen";
import OpenInFull from "@mui/icons-material/OpenInFull";
import { useTranslation } from "react-i18next";
import * as echarts from "echarts/core";
import { LinesChart, ScatterChart } from "echarts/charts";
import { GeoComponent, GridComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsCoreOption } from "echarts/core";
/** Natural Earth 110m countries (public domain), used as the IP-flow world map. */
import worldGeoJson from "../../../../assets/geo/world.json";
import {
  fitIpFlowView,
  ipFlowHasCoordinates,
  loopIpFlowCoords,
  nodePlaceLabel,
  resolveIpFlowCoordinates,
  sameIpFlowLocation,
} from "../../../../lib/network/dashboardIpFlow";
import type {
  IpFlowCenter,
  IpFlowEvent,
  IpFlowMatrix,
  IpFlowNode,
} from "../../../../lib/network/ipFlowMatrix";

echarts.use([ScatterChart, LinesChart, GeoComponent, GridComponent, TooltipComponent, CanvasRenderer]);
echarts.registerMap("world", worldGeoJson as never);

const TRAIL = 4;
const PLAY_MS = 1040;
const FLOW_PERIOD = 1.55;

const sweepSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const GEO_FILL = {
  aspectScale: 0.55,
  layoutCenter: ["50%", "50%"] as [string, string],
  layoutSize: "170%",
};

type IpFlowTone = {
  bg: string;
  text: string;
  muted: string;
  divider: string;
  internal: string;
  external: string;
  active: string;
  ring: string;
  cross: string;
  sweep: string;
  sweepFill: string;
  track: string;
  live: string;
  mapLand: string;
  mapBorder: string;
  severityLow: string;
  severityMedium: string;
  severityHigh: string;
  severityCritical: string;
};

type SeverityLineKey = "low" | "medium" | "high" | "critical";
type IpFlowTab = "replay" | "traffic";

function useIpFlowTone(): IpFlowTone {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const active = theme.palette.primary.main;
  const bg = theme.palette.background.paper;
  const text = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const divider = theme.palette.divider;
  const internal = theme.palette.info.main;
  const external = theme.palette.warning.main;
  const severityLow = theme.palette.success.main;
  const severityMedium = theme.palette.info.main;
  const severityHigh = theme.palette.warning.main;
  const severityCritical = theme.palette.error.main;

  return useMemo(
    () => ({
      bg,
      text,
      muted,
      divider,
      internal,
      external,
      active,
      ring: alpha(active, dark ? 0.34 : 0.26),
      cross: alpha(active, dark ? 0.18 : 0.14),
      sweep: active,
      sweepFill: alpha(active, dark ? 0.28 : 0.16),
      track: alpha(active, dark ? 0.18 : 0.12),
      live: alpha(active, dark ? 0.16 : 0.1),
      mapLand: alpha(text, dark ? 0.14 : 0.08),
      mapBorder: alpha(text, dark ? 0.32 : 0.22),
      severityLow,
      severityMedium,
      severityHigh,
      severityCritical,
    }),
    [
      active,
      bg,
      dark,
      divider,
      external,
      internal,
      muted,
      severityCritical,
      severityHigh,
      severityLow,
      severityMedium,
      text,
    ],
  );
}

function geoViewKey(option: EChartsCoreOption): string {
  const geo = (option as { geo?: { center?: unknown; zoom?: unknown } }).geo;
  return JSON.stringify({ center: geo?.center ?? null, zoom: geo?.zoom ?? null });
}

function ChartHost({
  option,
  keepView = false,
}: {
  option: EChartsCoreOption;
  keepView?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const viewKeyRef = useRef<string>("");

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const chart = echarts.init(host);
    chartRef.current = chart;
    viewKeyRef.current = "";
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(host);
    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (!keepView) {
      chart.setOption(option, { notMerge: true });
      chart.resize();
      return;
    }
    const nextView = geoViewKey(option);
    if (viewKeyRef.current !== nextView) {
      viewKeyRef.current = nextView;
      chart.setOption(option, { notMerge: true });
    } else {
      const { geo: _geo, ...rest } = option as EChartsCoreOption & { geo?: unknown };
      chart.setOption(rest, { replaceMerge: ["series"] });
    }
    chart.resize();
  }, [keepView, option]);

  return <Box ref={hostRef} sx={{ width: "100%", height: "100%" }} />;
}

function ringPoint(index: number, total: number, radius = 32) {
  const count = Math.max(total, 1);
  const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
  return {
    x: 50 + radius * Math.cos(angle),
    y: 50 + radius * Math.sin(angle),
  };
}

function shortIp(ip: string): string {
  if (ip.length <= 18) return ip;
  return `${ip.slice(0, 8)}…${ip.slice(-7)}`;
}

function severityKey(value?: string): SeverityLineKey | null {
  const key = value?.trim().toLowerCase();
  if (key === "low" || key === "medium" || key === "high" || key === "critical") {
    return key;
  }
  return null;
}

function severityColor(value: string | undefined, tone: IpFlowTone): string {
  switch (severityKey(value)) {
    case "low":
      return tone.severityLow;
    case "medium":
      return tone.severityMedium;
    case "high":
      return tone.severityHigh;
    case "critical":
      return tone.severityCritical;
    default:
      return tone.active;
  }
}

function formatEventTime(at: number, locale: string): string {
  if (!at) return "—";
  return new Intl.DateTimeFormat(locale, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(at));
}

function buildRadarOption(
  nodes: IpFlowNode[],
  trail: Array<{ event: IpFlowEvent; age: number }>,
  expanded: boolean,
  tone: IpFlowTone,
): EChartsCoreOption {
  const positions = new Map(
    nodes.map((node, index) => [node.ip, ringPoint(index, nodes.length, 32)]),
  );
  const active = trail.find((item) => item.age === 0)?.event;
  const showLabels = expanded || nodes.length <= 10;

  return {
    animation: false,
    backgroundColor: "transparent",
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    xAxis: { min: 0, max: 100, show: false, splitLine: { show: false } },
    yAxis: { min: 0, max: 100, show: false, inverse: true, splitLine: { show: false } },
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: tone.bg,
      borderColor: tone.divider,
      textStyle: {
        color: tone.text,
        fontSize: 12,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
    },
    series: [
      {
        type: "lines",
        coordinateSystem: "cartesian2d",
        zlevel: 1,
        silent: true,
        effect: { show: false },
        data: trail
          .filter((item) => item.age > 0)
          .flatMap((item) => {
            const from = positions.get(item.event.source);
            const to = positions.get(item.event.destination);
            if (!from || !to) return [];
            return [
              {
                coords: [
                  [from.x, from.y],
                  [to.x, to.y],
                ],
                lineStyle: {
                  width: Math.max(0.6, 2.2 - item.age * 0.35),
                  opacity: Math.max(0.12, 0.42 - item.age * 0.07),
                  curveness: 0.22,
                  color: item.event.sourceKind === "internal" ? tone.internal : tone.external,
                },
              },
            ];
          }),
        lineStyle: { curveness: 0.22 },
      },
      {
        type: "lines",
        coordinateSystem: "cartesian2d",
        zlevel: 2,
        silent: true,
        effect: {
          show: Boolean(active),
          trailLength: 0.38,
          symbol: "arrow",
          symbolSize: expanded ? 8 : 6,
          period: FLOW_PERIOD,
          color: tone.active,
        },
        data: active
          ? [
              {
                coords: [
                  [
                    positions.get(active.source)?.x ?? 50,
                    positions.get(active.source)?.y ?? 50,
                  ],
                  [
                    positions.get(active.destination)?.x ?? 50,
                    positions.get(active.destination)?.y ?? 50,
                  ],
                ],
              },
            ]
          : [],
        lineStyle: {
          width: 1.5,
          curveness: 0.22,
          color: active?.sourceKind === "internal" ? tone.internal : tone.external,
          opacity: 0.9,
        },
      },
      {
        type: "scatter",
        zlevel: 3,
        data: nodes.map((node) => {
          const point = positions.get(node.ip) ?? { x: 50, y: 50 };
          const isLive = node.ip === active?.source || node.ip === active?.destination;
          const color = node.kind === "internal" ? tone.internal : tone.external;
          return {
            name: node.ip,
            value: [point.x, point.y],
            kind: node.kind,
            itemStyle: {
              color,
              shadowBlur: isLive ? 12 : 0,
              shadowColor: color,
              borderColor: isLive ? tone.active : "transparent",
              borderWidth: isLive ? 2 : 0,
            },
            symbolSize: isLive ? (expanded ? 14 : 12) : expanded ? 9 : 8,
          };
        }),
        label: {
          show: showLabels,
          formatter: (item: { name?: string }) => shortIp(item.name ?? ""),
          color: tone.text,
          fontSize: expanded ? 11 : 9,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          position: "bottom",
          distance: 8,
        },
        tooltip: {
          formatter: (raw: unknown) => {
            const item = raw as { name?: string; data?: { kind?: string } };
            return `${item.name}<br/>${item.data?.kind === "internal" ? "INT" : "EXT"}`;
          },
        },
      },
    ],
  };
}

function asGeoCoord(point: { longitude: number; latitude: number }): [number, number] {
  return [point.longitude, point.latitude];
}

function geoCoord(
  point?: { longitude: number; latitude: number },
): [number, number] | null {
  return point ? asGeoCoord(point) : null;
}

function buildGeoOption(
  nodes: IpFlowNode[],
  center: IpFlowCenter | null | undefined,
  active: IpFlowEvent | undefined,
  expanded: boolean,
  tone: IpFlowTone,
): EChartsCoreOption {
  const positions = resolveIpFlowCoordinates(nodes, center);
  const nodeByIp = new Map(nodes.map((node) => [node.ip, node]));
  const fallback = positions.get("__center__") ?? { longitude: 20, latitude: 20 };
  const sourcePoint = (active && positions.get(active.source)) || fallback;
  const destinationPoint = (active && positions.get(active.destination)) || fallback;
  const samePlace = Boolean(active) && sameIpFlowLocation(sourcePoint, destinationPoint);
  const liveIps = new Set(active ? [active.source, active.destination] : []);
  const liveNodes = nodes.filter((node) => liveIps.has(node.ip));
  const scatterNodes = samePlace ? liveNodes.slice(0, 1) : liveNodes;
  const livePoints = [sourcePoint, destinationPoint];
  const view = fitIpFlowView(livePoints);
  const lineCoords = samePlace
    ? loopIpFlowCoords(sourcePoint, Math.min(0.32, 4.5 / view.zoom))
    : [asGeoCoord(sourcePoint), asGeoCoord(destinationPoint)];

  return {
    animation: false,
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: tone.bg,
      borderColor: tone.divider,
      textStyle: {
        color: tone.text,
        fontSize: 12,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
    },
    geo: {
      map: "world",
      roam: expanded,
      silent: true,
      ...GEO_FILL,
      center: view.center,
      zoom: view.zoom,
      itemStyle: {
        areaColor: tone.mapLand,
        borderColor: tone.mapBorder,
        borderWidth: 0.7,
      },
      emphasis: { disabled: true },
      select: { disabled: true },
      scaleLimit: { min: 1, max: 24 },
    },
    series: [
      {
        id: "ip-flow-active",
        type: "lines",
        coordinateSystem: "geo",
        zlevel: 2,
        silent: true,
        polyline: samePlace,
        effect: {
          show: Boolean(active),
          trailLength: 0.35,
          symbol: "arrow",
          symbolSize: expanded ? 10 : 8,
          period: FLOW_PERIOD,
          color: tone.active,
        },
        data: active ? [{ coords: lineCoords }] : [],
        lineStyle: {
          width: 1.8,
          curveness: samePlace ? 0 : 0.2,
          color: severityColor(active?.severity, tone),
          opacity: 0.95,
        },
      },
      {
        id: "ip-flow-nodes",
        type: "scatter",
        coordinateSystem: "geo",
        zlevel: 3,
        data: scatterNodes.flatMap((node) => {
          const point = geoCoord(positions.get(node.ip));
          if (!point) return [];
          const color = node.kind === "internal" ? tone.internal : tone.external;
          const place = nodePlaceLabel(node, center);
          const paired =
            samePlace && active && active.source !== active.destination
              ? `${shortIp(active.source)} → ${shortIp(active.destination)}`
              : shortIp(node.ip);
          return [
            {
              name: node.ip,
              value: point,
              kind: node.kind,
              place,
              labelText: paired,
              asSource: node.asSource,
              asDestination: node.asDestination,
              itemStyle: {
                color,
                shadowBlur: 14,
                shadowColor: color,
                borderColor: tone.active,
                borderWidth: 2,
              },
              symbolSize: expanded ? 14 : 12,
            },
          ];
        }),
        label: {
          show: true,
          formatter: (item: {
            name?: string;
            data?: { place?: string; labelText?: string };
          }) => {
            const ip = item.data?.labelText || shortIp(item.name ?? "");
            const place = item.data?.place?.trim();
            return place ? `${ip}\n${place}` : ip;
          },
          color: tone.text,
          fontSize: 10,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          position: "right",
          distance: 6,
        },
        tooltip: {
          formatter: (raw: unknown) => {
            const item = raw as {
              name?: string;
              data?: { kind?: string; place?: string };
            };
            const node = nodeByIp.get(item.name ?? "");
            const place = item.data?.place || nodePlaceLabel(node, center);
            const kind = item.data?.kind === "internal" ? "INT" : "EXT";
            const title =
              samePlace && active && active.source !== active.destination
                ? `${active.source} → ${active.destination}`
                : item.name;
            return [title, kind, place].filter(Boolean).join("<br/>");
          },
        },
      },
    ],
  };
}

function buildOverviewGeoOption(
  nodes: IpFlowNode[],
  center: IpFlowCenter | null | undefined,
  events: IpFlowEvent[],
  expanded: boolean,
  tone: IpFlowTone,
  focused?: IpFlowEvent,
): EChartsCoreOption {
  const positions = resolveIpFlowCoordinates(nodes, center);
  const links = new Map<
    string,
    {
      count: number;
      samePlace: boolean;
      from: { longitude: number; latitude: number };
      to: { longitude: number; latitude: number };
      severity?: string;
    }
  >();
  const endpoints = new Map<
    string,
    {
      point: { longitude: number; latitude: number };
      kind: IpFlowEvent["sourceKind"];
      count: number;
    }
  >();

  const bumpEndpoint = (
    point: { longitude: number; latitude: number },
    kind: IpFlowEvent["sourceKind"],
  ) => {
    const key = `${point.longitude.toFixed(3)},${point.latitude.toFixed(3)}`;
    const existing = endpoints.get(key);
    if (existing) {
      existing.count += 1;
      if (kind === "internal") existing.kind = "internal";
      return;
    }
    endpoints.set(key, { point, kind, count: 1 });
  };

  for (const event of events) {
    const from = positions.get(event.source);
    const to = positions.get(event.destination);
    if (!from || !to) continue;
    const samePlace = sameIpFlowLocation(from, to);
    const key = samePlace
      ? `loop:${from.longitude.toFixed(3)},${from.latitude.toFixed(3)}`
      : `${from.longitude.toFixed(3)},${from.latitude.toFixed(3)}>${to.longitude.toFixed(3)},${to.latitude.toFixed(3)}`;
    const existing = links.get(key);
    if (existing) existing.count += 1;
    else {
      links.set(key, {
        count: 1,
        samePlace,
        from,
        to,
        severity: event.severity,
      });
    }
    bumpEndpoint(from, event.sourceKind);
    if (!samePlace) bumpEndpoint(to, event.destinationKind);
  }

  const focusedFrom = focused ? positions.get(focused.source) : undefined;
  const focusedTo = focused ? positions.get(focused.destination) : undefined;
  const view = fitIpFlowView(
    focusedFrom && focusedTo
      ? [focusedFrom, focusedTo]
      : [...endpoints.values()].map((endpoint) => endpoint.point),
  );

  return {
    animation: false,
    backgroundColor: "transparent",
    tooltip: { show: false },
    geo: {
      map: "world",
      roam: true,
      silent: true,
      ...GEO_FILL,
      center: view.center,
      zoom: view.zoom,
      itemStyle: {
        areaColor: tone.mapLand,
        borderColor: tone.mapBorder,
        borderWidth: 0.6,
      },
      emphasis: { disabled: true },
      select: { disabled: true },
      scaleLimit: { min: 1, max: 24 },
    },
    series: [
      {
        id: "ip-flow-overview-lines",
        type: "lines",
        coordinateSystem: "geo",
        zlevel: 2,
        silent: true,
        effect: { show: false },
        data: [...links.values()].flatMap((link) => {
          if (link.samePlace) return [];
          const focusedLink =
            Boolean(focusedFrom) &&
            Boolean(focusedTo) &&
            sameIpFlowLocation(link.from, focusedFrom) &&
            sameIpFlowLocation(link.to, focusedTo);
          return [
            {
              coords: [asGeoCoord(link.from), asGeoCoord(link.to)],
              lineStyle: {
                width: focusedLink
                  ? 2.8
                  : Math.min(2.6, 0.8 + Math.log2(link.count + 1) * 0.45),
                opacity: focused ? (focusedLink ? 0.95 : 0.18) : 0.62,
                curveness: 0.18,
                color: severityColor(link.severity, tone),
              },
            },
          ];
        }),
        lineStyle: { curveness: 0.18, cap: "round" },
      },
      {
        id: "ip-flow-overview-nodes",
        type: "scatter",
        coordinateSystem: "geo",
        zlevel: 3,
        silent: true,
        tooltip: { show: false },
        data: [...endpoints.values()].map((endpoint) => {
          const focusedPoint =
            Boolean(focusedFrom) &&
            (sameIpFlowLocation(endpoint.point, focusedFrom) ||
              sameIpFlowLocation(endpoint.point, focusedTo));
          return {
            value: asGeoCoord(endpoint.point),
            count: endpoint.count,
            itemStyle: {
              color: endpoint.kind === "internal" ? tone.internal : tone.external,
              opacity: focused ? (focusedPoint ? 1 : 0.28) : 1,
            },
            symbolSize: focusedPoint ? (expanded ? 12 : 10) : expanded ? 8 : 7,
          };
        }),
        label: {
          show: true,
          formatter: (item: { data?: { count?: number } }) =>
            String(item.data?.count ?? ""),
          color: tone.text,
          fontSize: expanded ? 11 : 10,
          fontWeight: 700,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          position: "right",
          distance: 6,
        },
      },
    ],
  };
}

function PanelShell({
  children,
  minHeight,
  tone,
}: {
  children: ReactNode;
  minHeight?: { xs: number; lg: number } | number;
  tone: IpFlowTone;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        minWidth: 0,
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: tone.bg,
        color: tone.text,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      {children}
    </Box>
  );
}

function RadarStage({ option, tone }: { option: EChartsCoreOption; tone: IpFlowTone }) {
  const clipId = useId().replace(/:/g, "");

  return (
    <Box sx={{ flex: 1, minHeight: 0, containerType: "size", position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "min(100cqw, 100cqh)",
          height: "min(100cqw, 100cqh)",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 100 100"
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            "& .sweep-group": {
              transformBox: "view-box",
              transformOrigin: "50% 50%",
              animation: `${sweepSpin} 5.9s linear infinite`,
            },
          }}
        >
          <defs>
            <clipPath id={`${clipId}-clip`}>
              <circle cx="50" cy="50" r="47" />
            </clipPath>
          </defs>
          <circle cx="50" cy="50" r="47" fill={tone.live} />
          <line x1="50" y1="3" x2="50" y2="97" stroke={tone.cross} strokeWidth="0.35" />
          <line x1="3" y1="50" x2="97" y2="50" stroke={tone.cross} strokeWidth="0.35" />
          <circle cx="50" cy="50" r="16" fill="none" stroke={tone.ring} strokeWidth="0.45" />
          <circle cx="50" cy="50" r="32" fill="none" stroke={tone.ring} strokeWidth="0.45" />
          <circle cx="50" cy="50" r="47" fill="none" stroke={tone.ring} strokeWidth="0.7" />
          <g clipPath={`url(#${clipId}-clip)`}>
            <g className="sweep-group">
              <path d="M50 50 L50 3 A47 47 0 0 1 83.2 16.8 Z" fill={tone.sweepFill} />
              <line
                x1="50"
                y1="50"
                x2="50"
                y2="3"
                stroke={tone.sweep}
                strokeWidth="0.8"
                strokeLinecap="round"
              />
            </g>
          </g>
          <circle cx="50" cy="50" r="1.5" fill={tone.active} />
        </Box>
        <ChartHost option={option} />
      </Box>
    </Box>
  );
}

function MapStage({ option }: { option: EChartsCoreOption }) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
      <ChartHost option={option} keepView />
    </Box>
  );
}

function KindTag({ kind, tone }: { kind: "internal" | "external"; tone: IpFlowTone }) {
  const { t } = useTranslation();
  const color = kind === "internal" ? tone.internal : tone.external;
  return (
    <Box
      component="span"
      sx={{
        color,
        border: `1px solid ${color}`,
        px: 0.6,
        py: 0.1,
        fontSize: "0.625rem",
        letterSpacing: "0.08em",
        fontWeight: 700,
        borderRadius: 0.5,
      }}
    >
      {kind === "internal" ? t("dashboard.ipInternal") : t("dashboard.ipExternal")}
    </Box>
  );
}

function SeverityTag({ severity, tone }: { severity?: string; tone: IpFlowTone }) {
  const { t } = useTranslation();
  const key = severityKey(severity);
  if (!key) return null;
  const color = severityColor(severity, tone);
  return (
    <Box
      component="span"
      sx={{
        color,
        border: `1px solid ${color}`,
        px: 0.6,
        py: 0.1,
        fontSize: "0.625rem",
        letterSpacing: "0.08em",
        fontWeight: 700,
        borderRadius: 0.5,
      }}
    >
      {t(`dashboard.${key}`)}
    </Box>
  );
}

function EventReadout({
  event,
  index,
  total,
  locale,
  tone,
  sourcePlace,
  destinationPlace,
}: {
  event?: IpFlowEvent;
  index: number;
  total: number;
  locale: string;
  tone: IpFlowTone;
  sourcePlace?: string;
  destinationPlace?: string;
}) {
  const { t } = useTranslation();
  if (!event) return null;
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ color: tone.muted, fontSize: "0.65rem", letterSpacing: "0.12em" }}>
          {t("dashboard.ipFlowReplay")} · {index + 1}/{total} · {formatEventTime(event.at, locale)}
        </Typography>
        <SeverityTag severity={event.severity} tone={tone} />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ color: tone.text, fontSize: { xs: "0.8rem", md: "0.9rem" }, fontWeight: 700 }}>
          {event.source}
        </Typography>
        <KindTag kind={event.sourceKind} tone={tone} />
        <Typography sx={{ color: tone.active, fontSize: "0.9rem" }}>→</Typography>
        <Typography sx={{ color: tone.text, fontSize: { xs: "0.8rem", md: "0.9rem" }, fontWeight: 700 }}>
          {event.destination}
        </Typography>
        <KindTag kind={event.destinationKind} tone={tone} />
      </Stack>
      {sourcePlace || destinationPlace ? (
        <Typography sx={{ color: tone.muted, fontSize: "0.65rem" }}>
          {[sourcePlace, destinationPlace].filter(Boolean).join(" → ")}
        </Typography>
      ) : null}
    </Stack>
  );
}

function EventLog({
  events,
  activeIndex,
  locale,
  tone,
  nodeByIp,
  center,
  onSelect,
}: {
  events: IpFlowEvent[];
  activeIndex: number;
  locale: string;
  tone: IpFlowTone;
  nodeByIp: Map<string, IpFlowNode>;
  center?: IpFlowCenter | null;
  onSelect?: (index: number) => void;
}) {
  const { t } = useTranslation();
  const activeRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return events.flatMap((event, index) => {
      const sourcePlace = nodePlaceLabel(nodeByIp.get(event.source), center);
      const destinationPlace = nodePlaceLabel(nodeByIp.get(event.destination), center);
      if (needle) {
        const haystack = [
          event.source,
          event.destination,
          event.severity,
          sourcePlace,
          destinationPlace,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return [];
      }
      return [{ event, index, sourcePlace, destinationPlace }];
    });
  }, [center, events, nodeByIp, query]);

  useEffect(() => {
    const node = activeRef.current;
    if (!node) return;
    const root = node.parentElement;
    if (!root) return;
    const nodeTop = node.offsetTop;
    const nodeBottom = nodeTop + node.offsetHeight;
    if (nodeTop < root.scrollTop) root.scrollTop = nodeTop;
    else if (nodeBottom > root.scrollTop + root.clientHeight) {
      root.scrollTop = nodeBottom - root.clientHeight;
    }
  }, [activeIndex, rows]);

  return (
    <Stack
      spacing={0.75}
      sx={{
        minWidth: 0,
        width: { xs: "100%", md: 280 },
        minHeight: 0,
        maxHeight: "100%",
        height: "100%",
        overflow: "hidden",
        alignSelf: "stretch",
        borderLeft: { md: 1 },
        borderColor: { md: tone.divider },
        pl: { md: 1.25 },
        pr: { md: 0.5 },
      }}
    >
      <TextField
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t("dashboard.ipFlowSearch")}
        inputProps={{ "aria-label": t("dashboard.ipFlowSearch") }}
        sx={{
          flexShrink: 0,
          "& .MuiInputBase-root": {
            fontSize: "0.75rem",
            fontFamily: "inherit",
            bgcolor: tone.bg,
          },
        }}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {rows.length === 0 ? (
          <Typography sx={{ color: tone.muted, fontSize: "0.7rem", px: 1, py: 1 }}>
            {t("dashboard.ipFlowSearchEmpty")}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {rows.map(({ event, index, sourcePlace, destinationPlace }) => {
              const active = index === activeIndex;
              const places = [sourcePlace, destinationPlace].filter(Boolean).join(" → ");
              return (
                <Box
                  key={`${event.at}-${event.source}-${event.destination}-${index}`}
                  ref={active ? activeRef : undefined}
                  onClick={() => onSelect?.(index)}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderLeft: 2,
                    borderColor: active ? tone.active : "transparent",
                    bgcolor: active ? tone.live : "transparent",
                    opacity: active ? 1 : 0.58,
                    cursor: onSelect ? "pointer" : "default",
                  }}
                >
                  <Typography sx={{ color: tone.muted, fontSize: "0.65rem" }}>
                    {formatEventTime(event.at, locale)}
                    {event.severity ? ` · ${event.severity}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: tone.text, wordBreak: "break-all" }}>
                    {event.source} → {event.destination}
                  </Typography>
                  {places ? (
                    <Typography sx={{ color: tone.muted, fontSize: "0.65rem", wordBreak: "break-word" }}>
                      {places}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

function IpFlowBody({
  matrix,
  loading,
  expanded,
  tone,
  tab,
}: {
  matrix?: IpFlowMatrix;
  loading?: boolean;
  expanded: boolean;
  tone: IpFlowTone;
  tab: IpFlowTab;
}) {
  const { t, i18n } = useTranslation();
  const events = matrix?.events ?? [];
  const nodes = matrix?.nodes ?? [];
  const center = matrix?.center;
  const geo = ipFlowHasCoordinates(matrix);
  const nodeByIp = useMemo(() => new Map(nodes.map((node) => [node.ip, node])), [nodes]);
  const signature = `${events[0]?.at ?? 0}:${events.at(-1)?.at ?? 0}:${events.length}`;
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    setActiveIndex(0);
    setFocusedIndex(null);
  }, [signature]);

  useEffect(() => {
    if (tab !== "replay" || events.length < 2) return;
    const timerId = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % events.length);
    }, PLAY_MS);
    return () => window.clearInterval(timerId);
  }, [events.length, signature, tab]);

  const trail = useMemo(() => {
    if (events.length === 0) return [];
    const items: Array<{ event: IpFlowEvent; age: number }> = [];
    const depth = Math.min(TRAIL, events.length - 1);
    for (let age = depth; age >= 0; age -= 1) {
      const index = (activeIndex - age + events.length) % events.length;
      items.push({ event: events[index], age });
    }
    return items;
  }, [activeIndex, events]);

  const current = events[Math.min(activeIndex, Math.max(events.length - 1, 0))];
  const focused =
    focusedIndex != null ? events[focusedIndex] : undefined;
  const option = useMemo(() => {
    if (tab === "traffic" && geo) {
      return buildOverviewGeoOption(nodes, center, events, expanded, tone, focused);
    }
    return geo
      ? buildGeoOption(nodes, center, current, expanded, tone)
      : buildRadarOption(nodes, trail, expanded, tone);
  }, [center, current, events, expanded, focused, geo, nodes, tab, tone, trail]);

  if (loading && !matrix) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 280 }}>
        <CircularProgress size={24} sx={{ color: tone.active }} />
      </Stack>
    );
  }

  if (events.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 280, px: 3 }}>
        <Typography sx={{ color: tone.muted, textAlign: "center" }}>
          {t("dashboard.ipFlowEmpty")}
        </Typography>
      </Stack>
    );
  }

  const traffic = tab === "traffic";

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        display: "grid",
        alignItems: "stretch",
        gridTemplateColumns:
          expanded || traffic ? { xs: "1fr", md: "minmax(0, 1fr) 280px" } : "1fr",
        gridTemplateRows: expanded || traffic ? { xs: "minmax(0, 1fr) minmax(140px, 38%)", md: "minmax(0, 1fr)" } : "minmax(0, 1fr)",
        gap: expanded || traffic ? 1 : 1,
      }}
    >
      <Stack sx={{ minHeight: 0, minWidth: 0, height: "100%" }} spacing={1}>
        {geo ? (
          <MapStage key={tab} option={option} />
        ) : (
          <RadarStage option={option} tone={tone} />
        )}
        {traffic ? null : (
          <>
            <EventReadout
              event={current}
              index={activeIndex}
              total={events.length}
              locale={i18n.language}
              tone={tone}
              sourcePlace={nodePlaceLabel(nodeByIp.get(current.source), center)}
              destinationPlace={nodePlaceLabel(
                nodeByIp.get(current.destination),
                center,
              )}
            />
            <Box sx={{ height: 3, bgcolor: tone.track, borderRadius: 1, overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${((activeIndex + 1) / events.length) * 100}%`,
                  bgcolor: tone.active,
                  transition: "width 200ms linear",
                }}
              />
            </Box>
          </>
        )}
      </Stack>
      {expanded || traffic ? (
        <EventLog
          events={events}
          activeIndex={traffic ? focusedIndex ?? -1 : activeIndex}
          locale={i18n.language}
          tone={tone}
          nodeByIp={nodeByIp}
          center={center}
          onSelect={
            traffic
              ? (index) => setFocusedIndex(index)
              : (index) => setActiveIndex(index)
          }
        />
      ) : null}
    </Box>
  );
}

export default function DashboardIpFlowChart({
  matrix,
  loading = false,
}: {
  matrix?: IpFlowMatrix;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tone = useIpFlowTone();
  const [fullscreen, setFullscreen] = useState(false);
  const [tab, setTab] = useState<IpFlowTab>("replay");
  const tabs = (
    <Tabs
      value={tab}
      onChange={(_, value: IpFlowTab) => setTab(value)}
      sx={{
        minHeight: { xs: 48, sm: 34 },
        flexShrink: 0,
        px: 2,
        pr: { xs: 8, sm: 2 },
        "& .MuiTab-root": {
          minHeight: { xs: 48, sm: 34 },
          minWidth: { xs: 112, sm: 72 },
          py: 0.5,
          px: { xs: 2, sm: 1.5 },
          fontSize: { xs: "0.75rem", sm: "0.68rem" },
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontFamily: "inherit",
        },
      }}
    >
      <Tab value="replay" label={t("dashboard.ipFlowTabReplay")} />
      <Tab value="traffic" label={t("dashboard.ipFlowTabTraffic")} />
    </Tabs>
  );
  const internalCount =
    matrix?.stats?.internalCount ??
    matrix?.nodes.filter((node) => node.kind === "internal").length ??
    0;
  const externalCount =
    matrix?.stats?.externalCount ??
    matrix?.nodes.filter((node) => node.kind === "external").length ??
    0;
  const pairCount = matrix?.stats?.eventCount ?? matrix?.events.length ?? 0;
  const recordCount = matrix?.stats?.recordCount ?? matrix?.recordCount ?? 0;
  const centerLabel = matrix?.center?.label?.trim() ?? "";

  const header = (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1}
      sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.12em" }}>
          {t("dashboard.ipFlowTitle")}
        </Typography>
        <Typography sx={{ color: tone.muted, fontSize: "0.68rem", mt: 0.4, lineHeight: 1.4 }}>
          {t("dashboard.ipFlowSubtitle")}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 0.75, color: tone.muted, fontSize: "0.65rem" }}>
          {centerLabel ? <Box component="span">{t("dashboard.ipFlowCenter", { label: centerLabel })}</Box> : null}
          <Box component="span" sx={{ color: tone.internal }}>
            {t("dashboard.ipInternal")} {internalCount}
          </Box>
          <Box component="span" sx={{ color: tone.external }}>
            {t("dashboard.ipExternal")} {externalCount}
          </Box>
          <Box component="span">{t("dashboard.ipFlowPairs", { count: pairCount })}</Box>
          {recordCount > 0 ? (
            <Box component="span">{t("dashboard.ipFlowCount", { count: recordCount })}</Box>
          ) : null}
        </Stack>
      </Box>
      <Tooltip title={t("dashboard.ipFlowFullscreen")}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => setFullscreen(true)}
          aria-label={t("dashboard.ipFlowFullscreen")}
        >
          <OpenInFull fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <>
      <Box
        sx={{
          minWidth: 0,
          height: { xs: 420, lg: 480 },
          maxHeight: { xs: 420, lg: 480 },
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: tone.bg,
        }}
      >
        <PanelShell tone={tone}>
          {header}
          {tabs}
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%", px: tab === "traffic" ? 0 : 1, pb: 1, display: "flex", overflow: "hidden" }}>
            <IpFlowBody
              matrix={matrix}
              loading={loading}
              expanded={false}
              tone={tone}
              tab={tab}
            />
          </Box>
        </PanelShell>
      </Box>

      <Dialog
        fullScreen
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
      >
        <PanelShell tone={tone}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            sx={{ px: { xs: 1.5, md: 3 }, pt: 2.5, pb: 1.5 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", letterSpacing: "0.1em" }}>
                {t("dashboard.ipFlowTitle")}
              </Typography>
              <Typography sx={{ color: tone.muted, mt: 0.75, maxWidth: 720, fontSize: "0.8rem" }}>
                {t("dashboard.ipFlowSubtitle")}
              </Typography>
            </Box>
            <Tooltip title={t("dashboard.ipFlowExitFullscreen")}>
              <IconButton
                color="primary"
                onClick={() => setFullscreen(false)}
                aria-label={t("dashboard.ipFlowExitFullscreen")}
              >
                <CloseFullscreen />
              </IconButton>
            </Tooltip>
          </Stack>
          {tabs}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              px: tab === "traffic" ? 0 : { xs: 1, md: 2 },
              pb: tab === "traffic" ? 0 : 2,
              display: "flex",
            }}
          >
            <IpFlowBody matrix={matrix} loading={loading} expanded tone={tone} tab={tab} />
          </Box>
        </PanelShell>
      </Dialog>
    </>
  );
}
